import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DailyPickThresholds {
  min_market_cap: number;
  max_market_cap: number;
  min_consideration_score: number;
  min_pick_score: number;
  hot_pick_score: number;
  strong_contender_score: number;
}

export interface AppSetting<T = unknown> {
  id: string;
  value: T;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
}

export const useAppSettings = <T = unknown>(settingId: string) => {
  return useQuery({
    queryKey: ['app-settings', settingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .eq('id', settingId)
        .single();

      if (error) throw error;
      return data as AppSetting<T>;
    },
  });
};

export const useUpdateAppSetting = <T = unknown>() => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, value }: { id: string; value: T }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('app_settings')
        .update({ 
          value: value as never,
          updated_by: user?.id 
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['app-settings', variables.id] });
    },
  });
};

export const useDailyPickThresholds = () => {
  return useAppSettings<DailyPickThresholds>('daily_pick_thresholds');
};
